import CssBaseline from "@mui/joy/CssBaseline";
import { CssVarsProvider } from "@mui/joy/styles";
import {
  experimental_extendTheme as materialExtendTheme,
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { RowData } from "@tanstack/react-table";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import config from "./config";
import "@tanstack/react-table";

import queryClient from "./core/queryClient";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import theme from "./theme/theme";
import { installTouchDragPolyfill } from "./utils/touchDragPolyfill";

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPendingMinMs: 0,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

declare module "@tanstack/react-table" {
  // oxlint-disable-next-line typescript/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    // Used in routes table for our custom filters
    filterName?: string;

    // Used to add a classname to column cells
    className?: string;

    // Used by SortableTable: a column's fixed width in px, or, for a column without one, the
    // narrowest it may get while it takes up the rest of the table
    width?: number;
    minWidth?: number;
  }
}

Sentry.init({
  dsn: config.SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    // Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  environment: config.ENVIRONMENT,
});

const materialTheme = materialExtendTheme({
  typography: {
    allVariants: {
      fontFamily: "IBM Plex Sans",
    },
  },
  components: {
    // @ts-expect-error - not present in theme types
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "#c5c9d3",
          boxShadow:
            "0 0 #000,0px 1px 2px 0px rgba(21 21 21 / 0.08),0px 2px 4px 0px rgba(21 21 21 / 0.08)",
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          width: "280px",
          height: "314px",
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          paddingLeft: "12px",
          paddingRight: "0px",
          marginTop: 0,
        },
      },
    },
  },
});

// Render the app
const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MaterialCssVarsProvider
          theme={{ [MATERIAL_THEME_ID]: materialTheme }}
          defaultMode="light"
          defaultColorScheme="light"
        >
          <CssVarsProvider theme={theme} defaultColorScheme="light" defaultMode="light">
            {/* must be used under CssVarsProvider */}
            <CssBaseline />

            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </CssVarsProvider>
        </MaterialCssVarsProvider>
      </LocalizationProvider>
    </StrictMode>,
  );
}

installTouchDragPolyfill();

if (navigator.maxTouchPoints > 0) {
  // Add a long press handler that triggers onContextMenu
  document.addEventListener("touchstart", (e) => {
    console.log("TOUCHSTART");
    const target = e.target as HTMLElement;

    let didOpenCtx = false;

    const timeout = setTimeout(() => {
      didOpenCtx = true;
      if (target) {
        target.dispatchEvent(
          new MouseEvent("contextmenu", {
            bubbles: true,
            clientX: e.touches[0].clientX,
            clientY: e.touches[0].clientY,
          }),
        );
      }
    }, 500);

    const clear = (e: TouchEvent) => {
      if (didOpenCtx) {
        e.preventDefault();
        e.stopPropagation();
      }
      target.removeEventListener("touchmove", clear);
      target.removeEventListener("touchend", clear);
      target.removeEventListener("touchcancel", clear);
      clearTimeout(timeout);
    };

    target.addEventListener("touchmove", clear);
    target.addEventListener("touchend", clear);
    target.addEventListener("touchcancel", clear);
  });
}
